import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@/hooks/use-auth';

export interface CartItem {
  id: string;
  type: 'gps_tracker' | 'm2m_chip' | 'chip_plan';
  productId: string;
  productName: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  metadata?: {
    chipPlanId?: string;
    chipPlanName?: string;
    chipProductId?: string;
    monthlyRecurring?: number;
    billingCycle?: 'monthly' | 'yearly';
  };
}

interface CartState {
  items: CartItem[];
  totalItems: number;
  totalAmount: number;
  recurringAmount: number;
}

interface CartContextType {
  cart: CartState;
  addItem: (item: Omit<CartItem, 'id' | 'totalPrice'>) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  isInCart: (productId: string) => boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const { activeCompany } = useAuth();
  const [cart, setCart] = useState<CartState>({
    items: [],
    totalItems: 0,
    totalAmount: 0,
    recurringAmount: 0,
  });

  // Carregar carrinho do localStorage ao montar
  useEffect(() => {
    if (activeCompany?.id) {
      const savedCart = localStorage.getItem(`cart_${activeCompany.id}`);
      if (savedCart) {
        try {
          setCart(JSON.parse(savedCart));
        } catch (error) {
          console.error('Erro ao carregar carrinho:', error);
        }
      }
    }
  }, [activeCompany?.id]);

  // Salvar carrinho no localStorage sempre que mudar
  useEffect(() => {
    if (activeCompany?.id && cart.items.length > 0) {
      localStorage.setItem(`cart_${activeCompany.id}`, JSON.stringify(cart));
    }
  }, [cart, activeCompany?.id]);

  const calculateTotals = (items: CartItem[]) => {
    const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
    const totalAmount = items.reduce((sum, item) => sum + item.totalPrice, 0);
    const recurringAmount = items.reduce((sum, item) => 
      sum + (item.metadata?.monthlyRecurring || 0) * item.quantity, 0
    );
    return { totalItems, totalAmount, recurringAmount };
  };

  const addItem = (itemData: Omit<CartItem, 'id' | 'totalPrice'>) => {
    const newItem: CartItem = {
      ...itemData,
      id: crypto.randomUUID(),
      totalPrice: itemData.unitPrice * itemData.quantity,
    };

    const updatedItems = [...cart.items, newItem];
    setCart({
      items: updatedItems,
      ...calculateTotals(updatedItems),
    });
  };

  const removeItem = (itemId: string) => {
    const updatedItems = cart.items.filter(item => item.id !== itemId);
    setCart({
      items: updatedItems,
      ...calculateTotals(updatedItems),
    });
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity < 1) return;
    
    const updatedItems = cart.items.map(item =>
      item.id === itemId
        ? { ...item, quantity, totalPrice: item.unitPrice * quantity }
        : item
    );
    setCart({
      items: updatedItems,
      ...calculateTotals(updatedItems),
    });
  };

  const clearCart = () => {
    setCart({
      items: [],
      totalItems: 0,
      totalAmount: 0,
      recurringAmount: 0,
    });
    if (activeCompany?.id) {
      localStorage.removeItem(`cart_${activeCompany.id}`);
    }
  };

  const isInCart = (productId: string) => {
    return cart.items.some(item => item.productId === productId);
  };

  return (
    <CartContext.Provider value={{ cart, addItem, removeItem, updateQuantity, clearCart, isInCart }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};
